import _ from 'lodash';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

import { useTranslation } from 'ev-i18n';
import {
  Id,
  Practice,
  SignInUser,
  SupportedUserLanguages,
  User,
  UserCreator,
  UserLanguages,
  UserPreference,
  VisitParticipant,
} from 'ev-types';

import { UserResponseData } from 'ev-api/core';
import { EVColors } from 'ev-theme/styles';

type RoleCheckParameters = {
  user: User | VisitParticipant | undefined;
  practiceId: Id | undefined;
};

/**
 * Resolve the user's `customer_role` to the role for a specific customer (the
 * customer of the practice they're currently in). A user who belongs to more
 * than one customer has multiple customer roles; without this we'd read
 * whichever role the API returned first, so permissions like `access_admin`
 * could reflect the wrong customer.
 *
 * Returns the user unchanged when there's nothing to select from (no roles
 * array or no current customer yet). When the user has no role for the given
 * customer, `customer_role` becomes undefined — i.e. no customer-role
 * permissions in that practice, which is the correct (deny) outcome.
 */
export const selectCustomerRoleForCustomer = (
  user: User | undefined,
  customerId: Id | null | undefined,
): User | undefined => {
  if (!user) {
    return user;
  }
  const roles = user.customer_roles;
  if (!roles?.length || customerId == null) {
    return user;
  }
  const match = _.find(
    roles,
    role => String(role.customer_id) === String(customerId),
  );
  if (match?.id === user.customer_role?.id) {
    return user;
  }
  return { ...user, customer_role: match };
};

export const isPatient = (user: User | SignInUser | undefined) => {
  return user?.attributes.role === 'patient';
};

export const isAttendee = (user: User | undefined) => {
  return user?.attributes.role === 'attendee';
};

export const isPatientOrAttendee = (user: User | undefined) => {
  return isPatient(user) || isAttendee(user);
};

export const isAdminManagerOrOwner = (user: User, practice: Practice) => {
  return (
    isAdmin({ user: user, practiceId: practice.id }) ||
    isVisitManager({ user: user, practiceId: practice.id }) ||
    isOwner({ user: user, practiceId: practice.id })
  );
};

export const isGuarantorOfDependent = (
  guarantor: User | undefined,
  dependentId: Id,
): boolean => {
  if (!guarantor) {
    return false;
  }
  const dependentIds = guarantor.dependents.map(dep => dep.id);
  return dependentIds.includes(dependentId);
};

export const isDependentOfId = (dependent: User, guarantorId: Id): boolean => {
  return (
    dependent.attributes.is_dependent &&
    dependent.attributes.creator?.user_id === guarantorId
  );
};

function hasRole({
  user,
  role,
  practiceId,
}: {
  user: User | VisitParticipant | undefined;
  role: string;
  practiceId: Id | undefined;
}): boolean {
  return (
    user?.attributes.role === role ||
    _.some(
      user?.attributes.roles,
      ({ role: userRole, practice_id }) =>
        userRole === role && practice_id === practiceId,
    )
  );
}

export const creatorIsProvider = ({
  user,
}: {
  user: UserCreator | VisitParticipant;
}) => {
  function userCreatorIsAProvider(
    user: UserCreator | VisitParticipant,
  ): user is UserCreator {
    return !!(user as UserCreator).role;
  }

  function visitParticipantIsAProvider(
    user: UserCreator | VisitParticipant,
  ): user is VisitParticipant {
    return !!(user as VisitParticipant).attributes;
  }
  if (userCreatorIsAProvider(user)) {
    return user.role === 'physician' || user.role === 'staff';
  } else if (visitParticipantIsAProvider(user)) {
    return (
      user.attributes.role === 'physician' || user.attributes.role === 'staff'
    );
  }
  return false;
};

export const isProvider = ({ user, practiceId }: RoleCheckParameters) => {
  return (
    hasRole({ user, practiceId, role: 'physician' }) ||
    hasRole({ user, practiceId, role: 'staff' })
  );
};

export const isAdmin = ({
  user,
  practiceId,
}: {
  user: User;
  practiceId?: Id;
}) => {
  const customerRolesEnabled = getUserPreference(
    user,
    UserPreference.CustomerRoles,
  );
  if (customerRolesEnabled) {
    return !!user.customer_role?.access_admin;
  } else {
    return hasRole({ user, practiceId, role: 'admin' });
  }
};

export const isOwner = ({
  user,
  practiceId,
}: {
  user?: User;
  practiceId?: Id;
}) => {
  const customerRolesEnabled = getUserPreference(
    user,
    UserPreference.CustomerRoles,
  );
  if (customerRolesEnabled) {
    return user?.customer_role?.name === 'owner';
  } else {
    return hasRole({ user, practiceId, role: 'owner' });
  }
};

export const isVisitManager = ({
  user,
  practiceId,
}: {
  user?: User;
  practiceId?: Id;
}) => {
  const customerRolesEnabled = getUserPreference(
    user,
    UserPreference.CustomerRoles,
  );
  if (customerRolesEnabled) {
    return !!user?.customer_role?.manage_staff_visits;
  } else {
    return hasRole({ user, practiceId, role: 'visit_manager' });
  }
};

export const isScheduler = ({
  user,
  practiceId,
}: {
  user?: User;
  practiceId?: Id;
}) => {
  const customerRolesEnabled = getUserPreference(
    user,
    UserPreference.CustomerRoles,
  );
  if (customerRolesEnabled) {
    return !!user?.customer_role?.create_staff_visits;
  } else {
    return hasRole({ user, practiceId, role: 'scheduler' });
  }
};

export const isAdvocate = ({ user, practiceId }: RoleCheckParameters) => {
  return hasRole({ user, practiceId, role: 'advocate' });
};

export const isMember = ({
  user,
  practiceId,
}: {
  user: User;
  practiceId: Id;
}) => {
  const customerRolesEnabled = getUserPreference(
    user,
    UserPreference.CustomerRoles,
  );
  if (customerRolesEnabled) {
    return (
      !!user.customer_role?.access_app &&
      user.attributes.practice_ids.includes(practiceId)
    );
  }
  return hasRole({ user, practiceId, role: 'member' });
};

export function getUserPreference(
  user: User | undefined,
  preference: UserPreference,
) {
  const pref = _.find(user?.attributes.preferences, {
    name: preference,
  });

  if (pref) {
    return pref.value;
  }

  return null;
}

export function getUserInitials({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}) {
  // We filter the parts array in case there is an empty string
  const parts = [firstName, lastName]
    .filter(str => !!str)
    .map(str => str.trim());
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[1][0]}`;
  } else if (parts.length === 1) {
    return parts[0][0];
  }
  return '';
}
export function getCurrentUserLocale(user?: User) {
  const locale = user?.attributes.locale;
  return getCleanedUserLocale(locale);
}

export function getCleanedUserLocale(locale?: SupportedUserLanguages) {
  if (!locale) {
    return '';
  }
  if (locale === UserLanguages.Es_MX || locale === UserLanguages.En_US) {
    return locale.split('_')[0];
  }
  return locale;
}

export function getUserCompleteName({
  user,
  defaultUnknownUser,
  defaultUnknownCaller,
  isTelephonyUser,
}: {
  user: User | UserResponseData;
  defaultUnknownUser?: string;
  defaultUnknownCaller?: string;
  isTelephonyUser?: boolean;
}) {
  let { prefix, suffix } = user.attributes;
  const { full_name, first_name, last_name } = user.attributes;

  prefix = (prefix || '').trim().length > 0 ? `${prefix} ` : '';
  suffix = (suffix || '').trim().length > 0 ? `, ${suffix}` : '';

  if (first_name && last_name) {
    return [prefix, full_name, suffix].join('');
  } else if (first_name && !last_name) {
    return [prefix, first_name, suffix].join('');
  } else if (!first_name && last_name) {
    return [prefix, last_name, suffix].join('');
  } else if (isTelephonyUser) {
    return defaultUnknownCaller || '';
  } else {
    return defaultUnknownUser || '';
  }
}

export const useDobToAge = () => {
  const { t } = useTranslation();

  return useCallback(
    (date: DateTime): string => {
      const diff = DateTime.now()
        .diff(date, ['years', 'months', 'days'])
        .toObject();

      const years = diff.years || 0;
      const months = diff.months || 0;
      const days = Math.floor(diff.days || 0);

      if (years >= 1) {
        return t('{{years}} yrs', { years, count: years });
      } else if (months >= 1) {
        return t('{{months}} months', { months, count: months });
      } else {
        return t('{{days}} days', { days, count: days });
      }
    },
    [t],
  );
};

export const getUserStatusTextColor = (profileIsValid: boolean) =>
  profileIsValid ? EVColors.darkGreen : EVColors.darkRed;

export const getUserStatusBackgroundColor = (profileIsValid: boolean) =>
  profileIsValid ? EVColors.mint : EVColors.sakura;

export const useUserGetStatusText = () => {
  const { t } = useTranslation();
  return useCallback(
    (profileIsValid: boolean) =>
      profileIsValid ? t('REGISTERED') : t('PENDING'),
    [t],
  );
};

export const hasName = (firstName: string, lastName: string) =>
  !!firstName || !!lastName;

export const buildUserName = (
  firstName: string,
  lastName: string,
  suffix?: string | null,
  lastNameFirst: boolean = false,
) =>
  lastNameFirst
    ? suffix
      ? `${lastName}, ${firstName}, ${suffix}`
      : `${lastName}, ${firstName}`
    : suffix
      ? `${firstName} ${lastName}, ${suffix}`
      : `${firstName} ${lastName}`;

const buildSingleUserName = ({
  name,
  suffix,
}: {
  name: string;
  suffix?: string | null;
}) => {
  return suffix ? `${name}, ${suffix}` : name;
};

export const useGetUserName = ({
  firstName,
  lastName,
  suffix,
  lastNameFirst = false,
  isTelephonyUser = false,
}: {
  firstName: string | null;
  lastName: string | null;
  suffix?: string | null;
  lastNameFirst?: boolean;
  isTelephonyUser?: boolean;
}) => {
  const { t } = useTranslation();

  return useCallback(() => {
    if (firstName && lastName) {
      return buildUserName(firstName, lastName, suffix, lastNameFirst);
    } else if (
      firstName?.toLowerCase() !== 'unknown' &&
      (firstName || lastName)
    ) {
      return buildSingleUserName({
        name: firstName || lastName || '',
        suffix,
      });
    } else if (isTelephonyUser) {
      return t('Unknown Caller');
    } else {
      return t('Unknown User');
    }
  }, [firstName, lastName, suffix, lastNameFirst, isTelephonyUser, t]);
};
